import { mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import Modal from "components/Modal";
import PhoneNumber from "components/PhoneNumber";
import { routes } from "constants/routes";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { IInvitedCarrier } from "models/HaulPay";
import {
  getMatchedEmailCarriersAPI,
  sendCarrierInvitationAPI,
  updateInvitedUserAPI
} from "services/api/haulpay";
import styles from "./style.module.scss";

interface IProps {
  data: IInvitedCarrier;
  onInvitationDeleted: VoidFunction;
}

export default function InvitedCarrierItem({ data, onInvitationDeleted }: Readonly<IProps>) {
  const navigate = useNavigate();
  const [isResendModalOpen, toggleResendModal] = useToggle();
  const [isDeleteModalOpen, toggleDeleteModal] = useToggle();
  const [isDeletingInvite, setIsDeletingInvite] = useState(false);
  const [isResendingInvite, setIsResendingInvite] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  const connectPaymentProfile = async () => {
    setIsConnecting(true);
    try {
      if (data.payment_profile_ids.length) {
        // Create connection
        const { data: matchedResult } = await getMatchedEmailCarriersAPI({
          owner_email: data.email
        });
        if (matchedResult.results.length) {
          navigate(routes.HAULPAY_BROKER_CARRIERs_NEW_FLOW, {
            state: { type: "existing_profile", profiles: matchedResult.results }
          });
        }
      } else {
        // Create payment profile
        navigate(routes.HAULPAY_BROKER_CARRIERs_NEW_FLOW, {
          state: { type: "invited_profile", profile: data }
        });
      }
    } catch (err) {
      // Handle error if needed
      setIsConnecting(false);
    }
  };

  const resendInvitation = async (type: "email" | "phone_number") => {
    setIsResendingInvite(true);
    try {
      const pNumber = data.phone_number || phoneNumber;
      await sendCarrierInvitationAPI(
        data.group_id,
        type === "email" ? data.email : "",
        type === "phone_number" ? pNumber : ""
      );
      showSuccessMessage("Invite(s) successfully sent.");
      toggleResendModal();
    } catch (err: any) {
      showErrorMessage(err.response.data.error[0]);
    } finally {
      setIsResendingInvite(false);
    }
  };

  const handleDeleteInvitation = async () => {
    setIsDeletingInvite(true);
    try {
      await updateInvitedUserAPI(data.id, { deleted: true });
      showSuccessMessage("Invited user deleted.");
      toggleDeleteModal();
      onInvitationDeleted();
    } finally {
      setIsDeletingInvite(false);
    }
  };

  return (
    <div>
      <ul className={styles.invitedCard}>
        <li>
          <span className="font-bold">Status</span>
          <span>Invited</span>
        </li>
        <li>
          <span className="font-bold">Phone Number</span>
          <span>{data.phone_number}</span>
        </li>
        <li>
          <span className="font-bold">Email</span>
          <span className="break-all">{data.email}</span>
        </li>
        <li>
          <span className="font-bold">Last Invited</span>
          <span>{dayjs(data.last_invited).format("YYYY-MM-DD hh:mm:ss A")}</span>
        </li>
      </ul>

      <div className={styles.footer}>
        <Button
          rounded
          variant="outline"
          className="!w-fit text-xs !h-8 !px-3"
          onClick={connectPaymentProfile}
          loading={isConnecting}>
          {data.payment_profile_ids.length ? "Create Connection" : "Create Payment Profile"}
        </Button>
        <div className={styles.resend}>
          <Button rounded className="!w-fit text-xs !h-8 !px-5" onClick={toggleResendModal}>
            Resend
          </Button>
          <button onClick={toggleDeleteModal}>
            <Icon path={mdiTrashCanOutline} />
          </button>
        </div>
      </div>

      <Modal
        isOpen={isResendModalOpen}
        closeModal={toggleResendModal}
        wrapperClass="!my-auto desktop:!min-h-fit"
        dialogClass="!bg-foreground !opacity-95 !px-4 !py-8 gap-9"
        closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
        <div className="mt-10 flex flex-col gap-4.5">
          <span className="text-sm text-text">
            Would you like to send the invitation via Phone this time?
          </span>

          <PhoneNumber
            value={phoneNumber}
            placeholder="Enter Phone for Carrier"
            inputClass="!border-subtitle !rounded !p-4 h-7 text-sm"
            wrapperClass="flex-1"
            onChange={setPhoneNumber}
          />
        </div>
        <div className="flex justify-end gap-2.5 text-xs">
          <Button variant="outline" rounded onClick={toggleResendModal} className="h-9 !px-3">
            Cancel
          </Button>
          <Button
            rounded
            loading={isResendingInvite && phoneNumber === ""}
            className="h-9 !px-2"
            onClick={() => resendInvitation("email")}>
            No, Send via Email
          </Button>
          <Button
            rounded
            loading={isResendingInvite && phoneNumber !== ""}
            className="h-9 !px-2"
            onClick={() => resendInvitation("phone_number")}>
            Yes, Send via text
          </Button>
        </div>
      </Modal>

      <ConfirmationModal
        content={"Are you sure you want to delete this invited user."}
        isConfirmModalOpen={isDeleteModalOpen}
        toggleConfirmModal={toggleDeleteModal}
        onOK={handleDeleteInvitation}
        isLoading={isDeletingInvite}
        labelOKButton="Delete"
      />
    </div>
  );
}
